import { Injectable } from '@angular/core';
import { UserService } from './user.service';

export interface SignatureOptions {
  includeSignature: boolean;
  selectedDoctor?: any;
  groupUsers: any[];
}

export interface SignatureContext {
  canSign: boolean;
  isPhysician: boolean;
  hasSignaturePermission: boolean;
  signatureLabel: string;
  groupUsers: any[];
}

@Injectable({
  providedIn: 'root'
})
export class SignatureService {
  constructor(private userService: UserService) {}

  /**
   * Get the complete signature context for a component
   */
  getSignatureContext(): SignatureContext {
    return {
      canSign: this.userService.canUserSign,
      isPhysician: this.userService.isUserPhysician,
      hasSignaturePermission: this.userService.isNonPhysician,
      signatureLabel: this.getSignatureLabel(),
      groupUsers: this.userService.availableDoctorsList
    };
  }

  /**
   * Get the appropriate signature label based on user type
   */
  getSignatureLabel(): string {
    return this.userService.isUserPhysician ? 'Attach Signature' : 'Attach Doctor\'s Signature';
  }

  /**
   * Determine if signature should be included based on user permissions and checkbox state
   */
  shouldIncludeSignature(includeSignature: boolean): boolean {
    return this.userService.isNonPhysician || (includeSignature && this.userService.isUserPhysician);
  }

  /**
   * Get the signature image URL and name for the current context
   */
  getSignatureDetails(includeSignature: boolean, selectedDoctor?: any): { signatureUrl?: string; signatureName?: string } {
    if (!includeSignature) {
      return {};
    }

    if (this.userService.isUserPhysician) {
      const user = this.userService.user;
      return {
        signatureUrl: user?.signatureImageUrl,
        signatureName: `${user?.title || ''} ${user?.name || ''} ${user?.surname || ''}`.trim()
      };
    } else if (this.userService.isNonPhysician && selectedDoctor) {
      return {
        signatureUrl: selectedDoctor?.signatureImageUrl,
        signatureName: `${selectedDoctor?.title || ''} ${selectedDoctor?.name || ''} ${selectedDoctor?.surname || ''}`.trim()
      };
    }

    return {};
  }

  /**
   * Validate if signature can be included for the current user and context
   */
  canIncludeSignature(includeSignature: boolean, selectedDoctor?: any): boolean {
    if (!includeSignature) {
      return false;
    }

    if (this.userService.isUserPhysician) {
      return true;
    }

    if (this.userService.isNonPhysician && selectedDoctor) {
      return true;
    }

    return false;
  }

  /**
   * Get the default signature options for a component
   */
  getDefaultSignatureOptions(): SignatureOptions {
    return {
      includeSignature: false,
      selectedDoctor: null,
      groupUsers: this.userService.availableDoctorsList
    };
  }
} 